import React, { useState } from 'react';
import {
  ArrowRight,
  ShieldCheck,
  Zap,
  Globe,
  Smartphone,
  Layers,
  CheckCircle2,
  MessageSquare,
  Sparkles,
  Phone,
  Clock,
  Star
} from 'lucide-react';
import { SITE_CONTACT } from '../data/siteData';
import { CustomImage } from './CustomImage';

interface HeroProps {
  onOpenBooking: () => void;
  onExploreWork: () => void;
  onViewPricing?: () => void;
  onOpenPaybill?: () => void;
}

interface ShowcasePackage {
  id: 'websites' | 'apps' | 'erps';
  tabLabel: string;
  title: string;
  subtitle: string;
  priceDisplay: string;
  depositDisplay: string;
  turnaround: string;
  imageSrc: string;
  imageAlt: string;
  metricBadge: string;
  features: string[];
}

const SHOWCASE_PACKAGES: ShowcasePackage[] = [
  {
    id: 'websites',
    tabLabel: 'Websites (From 9K)',
    title: 'High-Converting Business Websites',
    subtitle: 'Custom modern design tailored to get Kenyan clients calling and buying.',
    priceDisplay: 'KES 9,000',
    depositDisplay: 'Lipa Pole Pole: 3,000/= deposit to start',
    turnaround: 'Live in 48 to 72 Hours',
    imageSrc: '/images/hero_digital_infrastructure_mobile.webp',
    imageAlt: 'High-converting business website design in Nairobi Kenya',
    metricBadge: '⚡ 48-Hour Live Delivery · Free SSL',
    features: [
      '100% Mobile & WhatsApp conversion optimized',
      'Free .co.ke or .com domain registration & 1 Year NVMe hosting',
      'Rank #1 in Google Local Maps & search results'
    ]
  },
  {
    id: 'apps',
    tabLabel: 'E-Commerce & Apps',
    title: 'Online Shops & Automotive Commerce',
    subtitle: 'Automated Safaricom Daraja Lipa na M-Pesa checkouts for instant sales.',
    priceDisplay: 'KES 35,000',
    depositDisplay: 'Lipa Pole Pole: Split into 3 monthly installments',
    turnaround: 'Delivered in 5 to 7 Days',
    imageSrc: '/images/jaymax_auto_ecommerce.webp',
    imageAlt: 'Jaymax Auto (jaymaxauto.co.ke) e-commerce auto spare parts website with M-Pesa checkout',
    metricBadge: '✓ Live at jaymaxauto.co.ke · M-Pesa Checkout',
    features: [
      'Automated M-Pesa Paybill & Till STK prompt on phone (Daraja 2.0)',
      'Automotive & retail catalogue with live search, filters & stock management',
      'Instant SMS customer order alerts, dispatch receipts & WhatsApp chat'
    ]
  },
  {
    id: 'erps',
    tabLabel: 'Custom ERPs',
    title: 'Excel-to-Cloud Portals & ERPs',
    subtitle: 'Stop operational chaos from broken spreadsheets and lost manual records.',
    priceDisplay: 'KES 45,000',
    depositDisplay: 'Lipa Pole Pole: Flexible milestone-based plan',
    turnaround: 'Delivered in 2 to 3 Weeks',
    imageSrc: '/images/custom_erp_dashboard_1790951335673.webp',
    imageAlt: 'Custom cloud ERP software and business database in Kenya',
    metricBadge: '🛡️ KRA eTIMS Ready · Multi-Branch Sync',
    features: [
      'Multi-branch stock, POS tills & staff permission control',
      'KRA eTIMS automated fiscal tax compliance',
      'Automated daily cloud backups with zero data loss'
    ]
  }
];

export const Hero: React.FC<HeroProps> = ({ onOpenBooking, onExploreWork, onViewPricing }) => {
  const [activeTabId, setActiveTabId] = useState<'websites' | 'apps' | 'erps'>('websites');

  const currentPackage = SHOWCASE_PACKAGES.find((p) => p.id === activeTabId) || SHOWCASE_PACKAGES[0];

  return (
    <section className="relative overflow-hidden bg-[#070c12] text-white pt-12 pb-16 lg:pt-16 lg:pb-24 border-b border-slate-800">
      
      {/* Cinematic Developer Coding Video Background with Ambient Glow */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        aria-hidden="true"
        style={{ width: '100%', height: '100%' }}
      >
        <video
          autoPlay
          muted
          playsInline
          loop
          preload="none"
          poster="/images/hero_video_poster.webp"
          className="hidden sm:block w-full h-full object-cover opacity-70 sm:opacity-75 scale-100"
          style={{ objectFit: 'cover', width: '100%', height: '100%' }}
        >
          <source src="/videos/hero-coding-guy.webm" type="video/webm" />
          <source src="/videos/hero-coding-guy.mp4" type="video/mp4" />
        </video>

        {/* Lightweight Mobile Poster: Zero background video download overhead on mobile devices */}
        <img
          src="/images/hero_video_poster.webp"
          alt=""
          aria-hidden="true"
          width="640"
          height="360"
          className="sm:hidden w-full h-full object-cover opacity-60"
          loading="lazy"
          decoding="async"
        />
        
        {/* Cinematic Vignette: Left backdrop for WCAG AAA text contrast, translucent center & right for clear video visibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#070c12]/92 via-[#070c12]/65 to-[#070c12]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070c12] via-transparent to-[#070c12]/50" />
      </div>

      {/* Background Subtle Grid */}
      <div className="absolute inset-0 bg-grid-pattern-dark opacity-10 pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          
          {/* Left Column: Direct Commercial Headline & Value Proposition */}
          <div className="lg:col-span-7 space-y-6 text-left">
            
            {/* Unboxed Kicker Metadata (Rule 3: Swiss Style Zero-Pill) */}
            <div className="flex items-center gap-2 text-xs font-mono text-[#fcd34d] tracking-wide">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse inline-block" />
              <span>Websites</span>
              <span aria-hidden="true" className="text-slate-600">/</span>
              <span>Apps</span>
              <span aria-hidden="true" className="text-slate-600">/</span>
              <span>Custom Software</span>
              <span aria-hidden="true" className="text-slate-600">/</span>
              <span>NVMe Hosting</span>
              <span aria-hidden="true" className="text-slate-600">/</span>
              <span className="text-slate-400">Nairobi, Kenya</span>
            </div>

            {/* H1 Main Heading: Akzidenz-Grotesk Display Architecture (Rule 1, Rule 7 & Rule 10) */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white font-serif-heading text-balance leading-[1.12]">
              Professional websites, apps &amp; systems that{' '}
              <span className="text-[#fcd34d] underline decoration-[#c59b4c]/60 underline-offset-8 phrase-keep">
                grow your sales.
              </span>
            </h1>

            {/* Quieter Supporting Subtitle: Helvetica Neutral (Rule 1 & Rule 10) */}
            <p className="max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed font-sans font-normal text-balance">
              Stop losing customers to outdated websites or manual paper registers. We engineer modern commercial platforms from <strong>KES 9,000</strong>, custom Android &amp; iOS mobile apps, and multi-branch ERP software with <strong>automated Lipa na M-Pesa</strong>. Split project costs in 3 convenient <strong>Lipa Pole Pole</strong> monthly installments.
            </p>

            {/* Primary Action Zone with Isolation Effect & Scarcity Signal (Rule 4 & Rule 8) */}
            <div className="pt-2 pb-1 space-y-3">
              <div className="flex flex-wrap items-center gap-3.5">
                <button
                  onClick={onOpenBooking}
                  className="isolated-cta inline-flex items-center justify-center gap-2.5 rounded-xl bg-[#c59b4c] hover:bg-[#b08535] px-7 py-4 text-base font-bold text-slate-950 transition-all swiss-shadow-gold cursor-pointer min-h-[48px]"
                >
                  <span className="phrase-keep">Get a Free Quotation</span>
                  <ArrowRight className="h-4 w-4 stroke-[2.5]" />
                </button>

                <button
                  onClick={onViewPricing || onExploreWork}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-900/90 hover:bg-slate-800 px-5 py-4 text-sm font-semibold text-white transition-colors cursor-pointer min-h-[48px]"
                >
                  <span>View Price Catalogue (From 9K)</span>
                </button>

                <a
                  href={SITE_CONTACT.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#009fe3]/15 border border-[#009fe3]/40 hover:bg-[#009fe3]/25 px-5 py-4 text-sm font-bold text-[#38bdf8] transition-colors cursor-pointer min-h-[48px]"
                >
                  <MessageSquare className="h-4 w-4 text-[#38bdf8]" />
                  <span>WhatsApp: +254 114 646 341</span>
                </a>
              </div>

              {/* Scarcity Signal (Rule 4) */}
              <div className="flex items-center gap-2 text-xs font-mono text-slate-400 pt-1">
                <span className="text-[#c59b4c] font-semibold">✦ Executive Intake:</span>
                <span>Strictly 3 client onboarding slots available this week · 48-Hour delivery queue active</span>
              </div>
            </div>

            {/* Credibility Metric Strip (Rule 1: Equalized & Quieter) */}
            <div className="pt-6 border-t border-slate-800/80 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div className="text-xl sm:text-2xl font-extrabold text-[#fcd34d] font-serif-heading tabular-nums">From 9,000/=</div>
                <div className="text-xs text-slate-400 font-normal">Affordable Packages</div>
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-extrabold text-white font-serif-heading tabular-nums">48 - 72h</div>
                <div className="text-xs text-slate-400 font-normal">Fast Live Delivery</div>
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-extrabold text-[#38bdf8] font-serif-heading tabular-nums">5.0 ★★★★★</div>
                <div className="text-xs text-slate-400 font-normal">Google Client Rating</div>
              </div>
              <div>
                <div className="text-xl sm:text-2xl font-extrabold text-emerald-400 font-serif-heading tabular-nums">M-Pesa</div>
                <div className="text-xs text-slate-400 font-normal">Lipa Pole Pole Ready</div>
              </div>
            </div>
          </div>

          {/* Right Column: Sleek, Minimalist, High-Converting Client Showcase & Booking Card */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-slate-700/80 bg-[#0d1622]/95 backdrop-blur-xl shadow-2xl p-5 sm:p-6 text-left space-y-5">
              
              {/* Header Status Bar */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-800/80 text-xs">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="font-mono text-slate-200 text-xs font-semibold">
                    Thinkers Arcade · Nairobi HQ
                  </span>
                </div>
                <span className="text-xs font-mono font-bold text-[#fcd34d] bg-[#c59b4c]/15 border border-[#c59b4c]/30 px-2.5 py-1 rounded-full">
                  LIPA POLE POLE AVAILABLE
                </span>
              </div>

              {/* Package Category Selector Tabs (Min 44px Touch Targets) */}
              <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-950 rounded-2xl border border-slate-800">
                {SHOWCASE_PACKAGES.map((pkg) => {
                  const isActive = pkg.id === activeTabId;
                  return (
                    <button
                      key={pkg.id}
                      onClick={() => setActiveTabId(pkg.id)}
                      className={`py-2.5 px-2 rounded-xl text-xs font-bold transition-all cursor-pointer min-h-[44px] flex items-center justify-center text-center ${
                        isActive
                          ? 'bg-[#c59b4c] text-slate-950 shadow-md'
                          : 'text-slate-300 hover:text-white hover:bg-slate-900'
                      }`}
                    >
                      <span>{pkg.tabLabel}</span>
                    </button>
                  );
                })}
              </div>

              {/* High-Resolution Client Showcase Image Preview */}
              <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 shadow-inner group">
                <div className="relative h-44 sm:h-52 w-full overflow-hidden">
                  <CustomImage
                    src={currentPackage.imageSrc}
                    alt={currentPackage.imageAlt}
                    width={560}
                    height={260}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    priority={true}
                    fallbackSrc="/images/hero_digital_infrastructure_mobile.webp"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
                </div>

                {/* Floating Live Metric Badge over Image */}
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-xs">
                  <span className="bg-black/85 backdrop-blur-md px-3 py-1.5 rounded-lg border border-white/10 text-white font-medium text-xs shadow-md">
                    {currentPackage.metricBadge}
                  </span>
                  <span className="bg-emerald-950/90 text-emerald-300 border border-emerald-800/80 px-2.5 py-1 rounded-lg text-xs font-mono font-bold">
                    {currentPackage.turnaround}
                  </span>
                </div>
              </div>

              {/* Package Details & Deliverables */}
              <div className="space-y-3 pt-1">
                <div className="flex items-baseline justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-white font-serif-heading">
                      {currentPackage.title}
                    </h3>
                    <p className="text-xs text-slate-300 mt-0.5">
                      {currentPackage.subtitle}
                    </p>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="text-2xl font-extrabold text-[#fcd34d] font-serif-heading tabular-nums">
                      {currentPackage.priceDisplay}
                    </div>
                  </div>
                </div>

                {/* Lipa Pole Pole Notice */}
                <div className="flex items-center gap-1.5 text-xs font-medium text-amber-300 bg-amber-500/10 border border-amber-500/20 px-3 py-1.5 rounded-xl">
                  <Clock className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                  <span>{currentPackage.depositDisplay}</span>
                </div>

                {/* Feature Bullet Points */}
                <ul className="space-y-2 text-xs sm:text-sm text-slate-200">
                  {currentPackage.features.map((feat, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Conversion Buttons: 1-Click Action */}
              <div className="space-y-2.5 pt-2 border-t border-slate-800/80">
                <button
                  onClick={() => onOpenBooking()}
                  className="w-full py-3.5 px-4 rounded-xl bg-[#c59b4c] hover:bg-[#b08535] text-slate-950 font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-md cursor-pointer min-h-[44px]"
                >
                  <span>Book This Project / Request Quote</span>
                  <ArrowRight className="h-4 w-4 text-slate-950" />
                </button>

                <a
                  href={`https://wa.me/254114646341?text=${encodeURIComponent(
                    `Hello Thinkers Arcade, I would like to book the ${currentPackage.title} (${currentPackage.priceDisplay}). Please advise on the timeline and onboarding.`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full py-3 px-4 rounded-xl border border-slate-700 bg-slate-900/90 hover:bg-slate-800 text-slate-200 font-semibold text-xs flex items-center justify-center gap-2 transition-colors cursor-pointer min-h-[44px]"
                >
                  <MessageSquare className="h-4 w-4 text-emerald-400" />
                  <span>Order via WhatsApp (+254 114 646 341)</span>
                </a>
              </div>

              {/* Trust Footer Bar */}
              <div className="pt-2 border-t border-slate-800/60 flex items-center justify-between text-xs text-slate-400 font-mono">
                <span className="flex items-center gap-1">
                  <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                  <span>5.0 Google Reviews</span>
                </span>
                <span>M-Pesa Paybill: <strong>542542</strong></span>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
